import { useUserStore } from '@itc/sdk';
import type { Pinia } from 'pinia';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { adminRoutes } from './admin/routes.ts';
import { modules } from './modules.ts';
import Forbidden from './pages/Forbidden.vue';
import Home from './pages/Home.vue';
import Login from './pages/Login.vue';

const routes = (pinia: Pinia): RouteRecordRaw[] => [
  { path: '/', component: Home, meta: { permission: 'authenticated', title: 'Home' } },
  { path: '/login', component: Login, meta: { permission: 'public', layout: 'bare', title: 'Log in' } },
  { path: '/forbidden', component: Forbidden, meta: { permission: 'authenticated', title: 'Not allowed' } },
  { path: '/inbox', component: () => import('./pages/Inbox.vue'), meta: { permission: 'authenticated', title: 'Inbox' } },
  { path: '/settings', component: () => import('./pages/MySettings.vue'), meta: { permission: 'authenticated', title: 'Your settings' } },
  adminRoutes(pinia),
  ...modules.flatMap((module) => module.routes),
  // Every @itc/ui primitive, for review. Not in production builds.
  ...(import.meta.env.DEV
    ? [{ path: '/dev/ui', component: () => import('./pages/DevUi.vue'), meta: { permission: 'public' as const, title: 'UI' } }]
    : []),
  { path: '/:pathMatch(.*)*', component: () => import('./pages/NotFound.vue'), meta: { permission: 'authenticated', title: 'Not found' } },
];

/** Only same-site paths, like the server's own check. */
function safeNext(next: unknown): string {
  return typeof next === 'string' && next.startsWith('/') && !next.startsWith('//') && !next.includes('\\') ? next : '/';
}

export function createAppRouter(pinia: Pinia) {
  const router = createRouter({ history: createWebHistory(), routes: routes(pinia) });

  // Pages declare who may open them. The server enforces the same rules on every
  // API call; this guard only decides what to show.
  router.beforeEach(async (to) => {
    const user = useUserStore(pinia);
    await user.ensureLoaded();
    if (to.path === '/login' && user.isLoggedIn) return safeNext(to.query['next']);
    const permission = to.meta.permission ?? 'authenticated';
    if (permission === 'public') return true;
    if (!user.isLoggedIn) return { path: '/login', query: { next: to.fullPath } };
    if (!user.can(permission)) return { path: '/forbidden', query: { from: to.fullPath } };
    return true;
  });

  router.afterEach((to) => {
    document.title = to.meta.title ? `${to.meta.title} · ITC Web` : 'ITC Web';
  });

  return router;
}
